// @flow

import React, { Fragment, useEffect, useRef, useState } from 'react';

import { ABTest } from '@zapier/toolbox-ab-testing';
import { getJson } from '@zapier/toolbox-browser-fetch';
import { isOffSite } from '@zapier/common-utils';
import { useTrackingContext } from '@zapier/toolbox-tracking';
import {
  Survey,
  useScreenSize,
} from '@zapier/design-system-marketing-and-paid';

import useLocalStorage from './useLocalStorage';

const POPUP_SURVEY_LOCALSTORAGE_KEY = 'zapier__popup-survey';
const EXPERIMENT_NAME = 'popup-survey';
const SURVEY_EVENTS = {
  shown: 'shown',
  dismissed: 'dismissed',
  completed: 'completed',
};

const SsrSurvey = () => {
  const hasSurveyAlreadyAppeared = useRef(false);
  const [showPopup, setShowPopup] = useState(false);
  const [userHasSignedUp, setUserHasSignedUp] = useState(true);
  const [surveySentSuccessfully, setSurveySentSuccessfully] = useState(false);
  const [
    showPopupFromLocalStorage,
    setShowPopupFromLocalStorage,
  ] = useLocalStorage(POPUP_SURVEY_LOCALSTORAGE_KEY);
  const size = useScreenSize();
  const { trackEvent } = useTrackingContext();

  async function getUserHasSignedUp() {
    try {
      /**
       * @alejandro
       * This will be refactored and moved to the proxy in MP-82
       * We didn't move this as part of MP-44 due to time constraints
       */
      const { has_signed_up: hasSignedUP } = await getJson('/api/v4/identity/');
      setUserHasSignedUp(hasSignedUP);
    } catch (e) {
      setUserHasSignedUp(false);
    }
  }

  useEffect(() => {
    getUserHasSignedUp();
  }, []);

  useEffect(() => {
    if (showPopupFromLocalStorage === null && !userHasSignedUp) {
      setShowPopup(true);
    }
  }, [showPopupFromLocalStorage, userHasSignedUp]);

  function trackOnMount() {
    if (!hasSurveyAlreadyAppeared.current) {
      trackEvent(`${EXPERIMENT_NAME}:${SURVEY_EVENTS.shown}`);
    }

    hasSurveyAlreadyAppeared.current = true;
  }

  function hidePopup() {
    setShowPopupFromLocalStorage(false);
    setShowPopup(false);
  }

  function dismissPopup() {
    hidePopup();
    trackEvent(`${EXPERIMENT_NAME}:${SURVEY_EVENTS.dismissed}`);
  }

  function handleSubmission(response: string) {
    trackEvent(`${EXPERIMENT_NAME}:${SURVEY_EVENTS.completed}`, {
      response,
    });
    setSurveySentSuccessfully(true);
  }

  function closeOnSuccess() {
    hidePopup();
  }

  return (
    <React.Fragment>
      {showPopup ? (
        <ABTest
          testName={EXPERIMENT_NAME}
          variantRenderers={{
            treatment: () => (
              <Survey
                dismissPopup={dismissPopup}
                dismissPopupOnClose={closeOnSuccess}
                onMount={trackOnMount}
                onSubmit={handleSubmission}
                size={size}
                surveySentSuccessfully={surveySentSuccessfully}
              />
            ),
            control: () => <Fragment />,
          }}
        />
      ) : null}
    </React.Fragment>
  );
};

const OnSiteOnly = () => (isOffSite() ? null : <SsrSurvey />);

export default OnSiteOnly;
